import { useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '../lib/api';
import { WorkshopForm, WORKSHOP_FORM_ID } from '../components/WorkshopForm';
import { useToast } from '../components/Toast';
import { StatusPill } from '../components/StatusPill';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { RegistrantsPanel } from '../components/RegistrantsPanel';
import { ZoomPanel } from '../components/ZoomPanel';
import { RecordingPanel } from '../components/RecordingPanel';
import { CoverCandidatePicker } from '../components/CoverCandidatePicker';
import { formatDateTime } from '../lib/format';

export function AdminEditPage() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const qc = useQueryClient();
  const [confirm, setConfirm] = useState<null | 'publish' | 'unpublish' | 'cancel' | 'remove'>(null);

  const query = useQuery({
    queryKey: ['admin', 'workshop', id],
    queryFn: () => api.admin.getWorkshop(id),
    enabled: id.length > 0,
  });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['admin', 'workshops'] });
    qc.invalidateQueries({ queryKey: ['admin', 'workshop', id] });
    qc.invalidateQueries({ queryKey: ['workshops'] });
  };

  // Load registrant count for the Cancel dialog so we can tell the admin
  // exactly how many people will get the cancellation email.
  const registrationsQuery = useQuery({
    queryKey: ['admin', 'registrations', id],
    queryFn: () => api.admin.listRegistrations(id),
    enabled: id.length > 0,
  });
  const registrantCount = registrationsQuery.data?.registrations.length ?? 0;

  const update = useMutation({
    mutationFn: (values: Parameters<typeof api.admin.updateWorkshop>[1]) =>
      api.admin.updateWorkshop(id, values),
    onSuccess: () => {
      toast.success('Changes saved');
      invalidate();
    },
    onError: (err) => toast.error('Save failed', err instanceof ApiError ? err.message : String(err)),
  });

  const publish = useMutation({
    mutationFn: () => api.admin.publishWorkshop(id),
    onSuccess: () => {
      toast.success('Published', 'Zoom meeting created. Workshop is now live on the public site.');
      invalidate();
    },
    onError: (err) => toast.error('Publish failed', err instanceof ApiError ? err.message : String(err)),
  });

  const unpublish = useMutation({
    mutationFn: () => api.admin.unpublishWorkshop(id),
    onSuccess: () => {
      toast.success('Moved back to draft', 'Zoom meeting was canceled.');
      invalidate();
    },
    onError: (err) => toast.error('Unpublish failed', err instanceof ApiError ? err.message : String(err)),
  });

  const cancel = useMutation({
    mutationFn: () => api.admin.deleteWorkshop(id),
    onSuccess: () => {
      toast.success(
        'Workshop canceled',
        registrantCount > 0
          ? `Sent cancellation emails to ${registrantCount} ${registrantCount === 1 ? 'registrant' : 'registrants'}. Their calendars will auto-remove the event.`
          : 'No registrants to notify.',
      );
      invalidate();
      navigate('/admin');
    },
    onError: (err) => toast.error('Cancel failed', err instanceof ApiError ? err.message : String(err)),
  });

  const remove = useMutation({
    mutationFn: () => api.admin.removePastWorkshop(id),
    onSuccess: () => {
      toast.success('Workshop removed', 'The past workshop and its registrations were deleted.');
      invalidate();
      navigate('/admin');
    },
    onError: (err) => toast.error('Remove failed', err instanceof ApiError ? err.message : String(err)),
  });

  const regen = useMutation({
    mutationFn: () => api.admin.regenerateImage(id),
    onSuccess: () => {
      toast.success('Cover image regenerated');
      invalidate();
    },
    onError: (err) => toast.error('Regen failed', err instanceof ApiError ? err.message : String(err)),
  });

  const upload = useMutation({
    mutationFn: (file: File) => api.admin.uploadCoverImage(id, file),
    onSuccess: () => {
      toast.success('Cover image uploaded', 'The AI-generated image was replaced.');
      invalidate();
    },
    onError: (err) => toast.error('Upload failed', err instanceof ApiError ? err.message : String(err)),
  });

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [pickerOpen, setPickerOpen] = useState(false);

  if (query.isLoading) {
    return (
      <div className="container-page max-w-3xl py-12">
        <div className="h-8 w-1/2 animate-pulse rounded bg-canvas" />
      </div>
    );
  }
  if (query.isError || !query.data) {
    return (
      <div className="container-page max-w-3xl py-12">
        <p className="text-sm text-red-600">Workshop not found.</p>
        <Link to="/admin" className="btn-secondary mt-4">Back to admin</Link>
      </div>
    );
  }

  const w = query.data.workshop;
  const canPublish = w.status === 'draft';
  const canUnpublish = w.status === 'scheduled';
  const canCancel = w.status === 'draft' || w.status === 'scheduled';
  const isPast = w.status === 'completed' || new Date(w.startsAt).getTime() < Date.now();

  return (
    <>
      <div className="container-page max-w-3xl py-10">
        <Link to="/admin" className="text-sm text-ink-muted hover:text-ink">← Back to admin</Link>

        <div className="mt-4 flex items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="flex items-center gap-3">
              <StatusPill status={w.status} />
              <h1 className="truncate text-2xl font-semibold tracking-tight">{w.title}</h1>
            </div>
            <p className="mt-1 text-xs text-ink-subtle">{formatDateTime(w.startsAt)} · {w.durationMin} min</p>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            {canPublish ? (
              <button
                className="btn-accent"
                disabled={publish.isPending}
                onClick={() => setConfirm('publish')}
              >
                {publish.isPending ? 'Publishing…' : 'Publish'}
              </button>
            ) : null}
            {canUnpublish ? (
              <button
                className="btn-secondary"
                disabled={unpublish.isPending}
                onClick={() => setConfirm('unpublish')}
              >
                {unpublish.isPending ? 'Unpublishing…' : 'Unpublish'}
              </button>
            ) : null}
            {w.status === 'draft' || w.status === 'canceled' ? (
              <Link
                to={`/admin/preview/${w.slug}`}
                className="btn-secondary"
                target="_blank"
                rel="noreferrer"
              >
                Preview →
              </Link>
            ) : (
              <Link to={`/w/${w.slug}`} className="btn-secondary" target="_blank" rel="noreferrer">
                View public →
              </Link>
            )}
          </div>
        </div>

        {/* Provisioning status surfaces at the top — Zoom meeting + cover image
            are the two things an admin most wants to verify after saving. Both
            self-handle their "not yet provisioned" / "needs retry" states. */}
        <div className="mt-6 space-y-4">
          <ZoomPanel workshop={w} />
        </div>

        <div className="mt-8">
          <h2 className="mb-4 text-sm font-semibold uppercase tracking-wider text-ink-subtle">Details</h2>
          <WorkshopForm
            initial={w}
            onSubmit={(values) => update.mutate(values)}
            submitting={update.isPending}
            submitLabel="Save changes"
            cancelHref="/admin"
            error={update.isError && update.error instanceof ApiError ? update.error.message : null}
            hideActions
          />
        </div>

        <div className="mt-8 rounded-2xl border border-line bg-surface p-5">
          <div className="flex items-center justify-between gap-3">
            <div>
              <h3 className="text-sm font-semibold">Cover image</h3>
              <p className="text-xs text-ink-muted">
                {w.coverImageUrl ? 'Regenerate to try a new AI variant, or upload your own.' : 'Not yet generated. Publishing creates one — or upload your own now.'}
              </p>
            </div>
            <div className="flex shrink-0 items-center gap-2">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/png,image/jpeg,image/webp"
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) upload.mutate(file);
                  // Reset so picking the same file twice fires the handler again.
                  e.target.value = '';
                }}
              />
              <button
                className="btn-secondary"
                disabled={upload.isPending}
                onClick={() => fileInputRef.current?.click()}
              >
                {upload.isPending ? 'Uploading…' : 'Upload'}
              </button>
              <button className="btn-secondary" disabled={regen.isPending} onClick={() => regen.mutate()}>
                {regen.isPending ? 'Regenerating…' : 'Regenerate'}
              </button>
              {/* Best-of-3 is the recommended path: generates 3 candidates in
                  parallel (~30s) and lets the admin pick. Single Regenerate is
                  kept around for when an admin already knows the prompt is in
                  a good spot and just wants a fresh roll. */}
              <button
                className="btn-accent"
                onClick={() => setPickerOpen(true)}
              >
                Generate
              </button>
            </div>
          </div>
          {w.coverImageUrl ? (
            <img src={w.coverImageUrl} alt="" className="mt-4 aspect-video w-full rounded-xl object-cover" />
          ) : null}
        </div>

        {/* Save row lives BELOW the cover image so the details form, tags,
            guest, AND cover all save together — the admin completes all the
            workshop content before committing changes. Cancel on the left,
            Save on the right (standard admin form convention). The button
            uses `form={WORKSHOP_FORM_ID}` to submit the form even though
            it's visually separated from it. */}
        <div className="mt-10 flex items-center justify-between gap-3">
          <Link to="/admin" className="btn-secondary">Cancel</Link>
          <button
            type="submit"
            form={WORKSHOP_FORM_ID}
            className="btn-primary"
            disabled={update.isPending}
          >
            {update.isPending ? 'Saving…' : 'Save changes'}
          </button>
        </div>

        {/* Recording fallback — visible only when the workshop is past, so admins
            can paste a Zoom share URL if the webhook never delivered. */}
        {(w.status === 'completed' || new Date(w.startsAt).getTime() < Date.now()) ? (
          <div className="mt-6">
            <RecordingPanel workshop={w} onChange={invalidate} />
          </div>
        ) : null}

        {/* Soft divider — separates "this workshop's content" (above) from
            "people + destructive actions" (below). */}
        <hr className="my-12 border-t border-line" />

        <div>
          <RegistrantsPanel workshopId={w.id} workshopTitle={w.title} />
        </div>

        <div className="mt-12 rounded-2xl border border-red-200 bg-red-50/50 p-5">
          <h3 className="text-sm font-semibold text-red-900">Danger zone</h3>
          {isPast ? (
            <>
              <p className="mt-1 text-xs text-red-900/70">
                Removing deletes this past workshop from admin and public history, along with its
                registrations. No attendee emails are sent.
              </p>
              <button
                className="mt-3 inline-flex items-center justify-center rounded-full bg-white px-4 py-2 text-sm font-semibold text-red-700 transition hover:bg-red-100 disabled:opacity-50"
                disabled={remove.isPending}
                onClick={() => setConfirm('remove')}
              >
                {remove.isPending ? 'Removing…' : 'Remove workshop'}
              </button>
            </>
          ) : (
            <>
              <p className="mt-1 text-xs text-red-900/70">
                Canceling removes the workshop from public listings, cancels the Zoom meeting
                {registrantCount > 0
                  ? `, and emails all ${registrantCount} ${registrantCount === 1 ? 'registrant' : 'registrants'} a cancellation notice that auto-removes the event from their calendar.`
                  : '. No registrants yet — no emails sent.'}
              </p>
              <button
                className="mt-3 inline-flex items-center justify-center rounded-full bg-white px-4 py-2 text-sm font-semibold text-red-700 transition hover:bg-red-100 disabled:opacity-50"
                disabled={!canCancel || cancel.isPending}
                onClick={() => setConfirm('cancel')}
              >
                {cancel.isPending ? 'Canceling…' : canCancel ? 'Cancel workshop' : 'Already canceled / completed'}
              </button>
            </>
          )}
        </div>
      </div>

      <ConfirmDialog
        open={confirm === 'publish'}
        title="Publish this workshop?"
        body="Provisions a Zoom meeting (shared attendee link, waiting room and cloud recording on) and generates the AI cover image. The workshop becomes visible on the public site immediately."
        confirmLabel="Publish"
        onConfirm={() => {
          setConfirm(null);
          publish.mutate();
        }}
        onCancel={() => setConfirm(null)}
      />
      <ConfirmDialog
        open={confirm === 'unpublish'}
        title="Move back to draft?"
        body="The linked Zoom meeting will be deleted. Anyone who registered will lose their join link. The cover image is preserved."
        confirmLabel="Unpublish"
        destructive
        onConfirm={() => {
          setConfirm(null);
          unpublish.mutate();
        }}
        onCancel={() => setConfirm(null)}
      />
      <ConfirmDialog
        open={confirm === 'cancel'}
        title="Cancel this workshop?"
        body={
          registrantCount > 0
            ? `${registrantCount} ${registrantCount === 1 ? 'person has' : 'people have'} registered. Canceling will email ${registrantCount === 1 ? 'them' : 'all of them'} a cancellation notice — their calendar event will auto-remove. The Zoom meeting is deleted. This cannot be undone.`
            : "The workshop is removed from public listings and the linked Zoom meeting is canceled. No one has registered yet, so no emails are sent. This cannot be undone."
        }
        confirmLabel={
          registrantCount > 0
            ? `Cancel & notify ${registrantCount}`
            : 'Cancel workshop'
        }
        destructive
        onConfirm={() => {
          setConfirm(null);
          cancel.mutate();
        }}
        onCancel={() => setConfirm(null)}
      />
      <ConfirmDialog
        open={confirm === 'remove'}
        title="Remove this past workshop?"
        body={
          registrantCount > 0
            ? `This permanently deletes the workshop and ${registrantCount} ${registrantCount === 1 ? 'registration' : 'registrations'}. No attendee emails are sent.`
            : 'This permanently deletes the workshop. No attendee emails are sent.'
        }
        confirmLabel="Remove workshop"
        destructive
        onConfirm={() => {
          setConfirm(null);
          remove.mutate();
        }}
        onCancel={() => setConfirm(null)}
      />

      <CoverCandidatePicker
        workshopId={w.id}
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onSelected={invalidate}
      />
    </>
  );
}
